import { IDBScanParameters, PointType } from '../db-scan-interfaces';
import {
    euclideanDistance,
    euclideanDistanceExplanation,
    manhattanDistance,
    manhattanDistanceExplanation,
    round
} from '../../cluster-provider';
import { DistanceType } from '../../cluster-interfaces';

const hasCorePoint = (pointTypesMap: Map<string, PointType>, set: Set<string>): boolean => {
    for(const innerPoint of set) {
        if(pointTypesMap.get(innerPoint) === PointType.CorePoint) {
            return true;
        }
    }

    return false;
};

const setOtherPoints = (params: IDBScanParameters, clusterMap: Map<string, Set<string>>, pointTypesMap: Map<string, PointType>) => {
    for(const point of params.points) {
        const set = clusterMap.get(point[0]) || new Set();

        if(set.size >= params.minPoints) continue;

        if(hasCorePoint(pointTypesMap, set)) {
            pointTypesMap.set(point[0], PointType.BorderPoint);
        }
        else{
            pointTypesMap.set(point[0], PointType.NoisePoint);
        }
    }
};

/*export const areNumberSetsEqual = (set1: Set<unknown>, set2: Set<unknown>): boolean => {
    if (set1.size !== set2.size) {
        return false;
    }

    for (const val of set1) {
        if (!set2.has(val)) {
            return false;
        }
    }

    return true;
};

export const isSubset = (subset: Set<unknown>, superset: Set<unknown>): boolean => {
    for (const val of subset) {
        if (!superset.has(val)) {
            return false;
        }
    }
    return true;
};*/

const getCluster = (
    results: Set<string>[],
    set: Set<string>,
    pointTypesMap: Map<string, PointType>
): Set<string>|null => {
    if(results.length <= 0) return null;

    for(const letter of set) {
        const type = pointTypesMap.get(letter);
        if(type !== PointType.CorePoint) continue;

        for(const result of results) {
            if(result.has(letter)) {
                return result;
            }
        }
    }
    return null;
};

export const findClusters = (
    clusterMap: Map<string, Set<string>>,
    pointTypesMap: Map<string, PointType>
): string[][] => {
    const results: Set<string>[] = [];

    for(const [letter, set] of clusterMap) {
        const copy = new Set(set);
        const type = pointTypesMap.get(letter);
        if(type !== PointType.CorePoint) continue;

        const foundCluster = getCluster(results, set, pointTypesMap);
        if(foundCluster) {
            for(const setLetter of copy) {
                foundCluster.add(setLetter);
            }
        }
        else{
            results.push(copy);
        }
    }

    for(const [letter, set] of clusterMap) {
        const copy = new Set(set);
        const type = pointTypesMap.get(letter);

        if(type !== PointType.BorderPoint) continue;

        const foundCluster = getCluster(results, copy, pointTypesMap);
        if(foundCluster) {
            foundCluster.add(letter);
        }
    }

    return results.map(set => [...set]);
};

export const getTableData = (params: IDBScanParameters, distance: DistanceType) : {
    map: Map<string, number>;
    mapCalculations: string[];
    clusterMap: Map<string, Set<string>>;
    pointTypesMap: Map<string, PointType>;
    clusters: string[][];
} => {
    const map: Map<string, number> = new Map();
    const mapCalculations: string[] = [];
    const clusterMap: Map<string, Set<string>> = new Map();
    const pointTypesMap: Map<string, PointType> = new Map();

    for(const point1 of params.points) {

        const set = clusterMap.get(point1[0]) || new Set();

        for(const point2 of params.points) {

            let d = 0;

            if(distance === DistanceType.Manhattan) {
                d = manhattanDistance([point1[1], point1[2]], [point2[1], point2[2]]);
                const calc = manhattanDistanceExplanation([point1[1], point1[2]], [point2[1], point2[2]]);
                mapCalculations.push(`d(${ point1[0] }, ${ point2[0] }) = ${ calc }`);
            }
            else{
                d = euclideanDistance([point1[1], point1[2]], [point2[1], point2[2]]);
                const calc = euclideanDistanceExplanation([point1[1], point1[2]], [point2[1], point2[2]]);
                mapCalculations.push(`d(${ point1[0] }, ${ point2[0] }) = ${ calc }`);
            }

            d = round(d);

            map.set(point1[0] + '-' + point2[0], d);

            // console.log('point1', point1[0], 'd', d, 'params.epsilon', params.epsilon, 'd <= params.epsilon', d <= params.epsilon)

            if(d <= params.epsilon) {
                set.add(point2[0]);

                // console.log(point1[0], point2[0], 'params.epsilon', params.epsilon, 'd', d)
            }
        }

        clusterMap.set(point1[0], set);
    }

    // Define core points ------------
    for(const point of params.points) {
        const set = clusterMap.get(point[0]) || new Set();
        if(set.size >= params.minPoints) {
            pointTypesMap.set(point[0], PointType.CorePoint);
        }
    }

    setOtherPoints(params, clusterMap, pointTypesMap);

    // Find clusters --------------
    const clusters: string[][] = findClusters(clusterMap, pointTypesMap);

    return {
        map,
        mapCalculations,
        clusterMap,
        pointTypesMap,
        clusters,
    };
};

export const getPointTypeName = (pointType: PointType): string => {
    switch(pointType) {
        case PointType.BorderPoint: {
            return 'BorderPoint';
        }
        case PointType.NoisePoint: {
            return 'NoisePoint';
        }
        case PointType.CorePoint: {
            return 'CorePoint';
        }
    }

    return '';
};